import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { AppShell } from './layout/app-shell';
import { NotFound } from './layout/not-found';
import { RouteErrorBoundary } from './layout/route-error-boundary';
import { RequireAuth } from './layout/require-auth';
import { RequireAbility, type AbilityHandle } from './layout/require-ability';
import { NAV_ITEMS } from '../auth/policy';

/**
 * The route tree (docs/07-implementation-roadmap.md F0.7, F1.4, F1.8).
 *
 * Three rules hold from here on:
 *
 *  - **Every route element is lazy.** F-DoD-7 budgets the initial bundle at
 *    350 kB gzipped and a route added without lazy-loading fails that check.
 *    Doing it from the first route means no later sprint has to retrofit it.
 *  - **`errorElement` sits on the layout route**, so a failure anywhere below
 *    renders inside the shell with the support code, rather than replacing the
 *    whole page with a blank screen.
 *  - **Every authenticated route declares the ability it needs** in its
 *    `handle`, and `RequireAbility` enforces it. The gate lives in this table,
 *    next to the path, so the screen underneath can be replaced by a later
 *    sprint without the gate going with it.
 *
 * Deep links survive a reload because Caddy answers every unknown path with
 * `index.html` (`try_files` in `frontend/Caddyfile`) — without it the server
 * returns its own 404 for `/anything` and the router never runs.
 */

/**
 * Members (docs/07-implementation-roadmap.md F2), the first navigation entry to be
 * replaced in place: the path, its sidebar entry and the `members.list` gate are
 * exactly as F1 left them, and only the `lazy` import changed.
 *
 * The detail route declares the narrower ability it needs. `RequireAbility`
 * takes the innermost `handle` among the matched routes, so `/jemaat/:id` is
 * gated on `members.read` while the section stays gated on `members.list` —
 * without the section having to restate anything.
 */
const memberRoutes: RouteObject[] = [
  {
    path: 'jemaat',
    handle: { ability: 'members.list' } satisfies AbilityHandle,
    Component: RequireAbility,
    children: [
      {
        index: true,
        lazy: async () => ({ Component: (await import('../features/members/member-list-page')).default }),
      },
      {
        path: ':id',
        handle: { ability: 'members.read' } satisfies AbilityHandle,
        lazy: async () => ({ Component: (await import('../features/members/member-detail-page')).default }),
      },
      {
        path: ':id/ubah',
        handle: { ability: 'members.update' } satisfies AbilityHandle,
        Component: RequireAbility,
        children: [
          {
            index: true,
            lazy: async () => ({ Component: (await import('../features/members/member-edit-page')).default }),
          },
        ],
      },
    ],
  },
];

/**
 * Families and regions (docs/07-implementation-roadmap.md F4), replaced in place the
 * same way F2 replaced members: the paths, sidebar entries and gates are exactly
 * as F1 left them, and only the `lazy` imports changed.
 *
 * **Neither section has a `tambah` route any more.** Every new member and every
 * new household is created through Atestasi Masuk (`/atestasi-masuk`), which
 * builds the whole package before writing any of it — the only place a rule
 * like "a household must have somebody in it" can be enforced (docs/02 rule 18).
 * Two ways in would have been two answers to what a valid record is, and the
 * one that skipped the package is the one that could still make an empty
 * household.
 *
 * The section is gated on `families.list`, which excludes PASTOR by design
 * (docs/04 §3). The detail route drops to `families.read`, which does not — a
 * pastor reaches one household through the member they are looking at, via the
 * household panel's link, and `RequireAbility` takes the innermost `handle` among
 * the matched routes so that works without the section restating anything.
 */
const familyRoutes: RouteObject[] = [
  {
    path: 'keluarga',
    handle: { ability: 'families.list' } satisfies AbilityHandle,
    Component: RequireAbility,
    children: [
      {
        index: true,
        lazy: async () => ({ Component: (await import('../features/families/family-list-page')).default }),
      },
      {
        path: ':id/ubah',
        handle: { ability: 'families.update' } satisfies AbilityHandle,
        Component: RequireAbility,
        children: [
          {
            index: true,
            lazy: async () => ({ Component: (await import('../features/families/family-edit-page')).default }),
          },
        ],
      },
    ],
  },
  /**
   * The detail route sits **outside** the `families.list` section, at the same
   * path, so a PASTOR can reach it.
   *
   * Nesting it under `keluarga` would have put it behind `families.list`, and a
   * pastor following the household link from a member record — which docs/04 §3
   * explicitly allows — would have been turned away by the client for something
   * the server permits. React Router matches the more specific of two sibling
   * paths, so `/keluarga` still resolves to the list.
   */
  {
    path: 'keluarga/:id',
    handle: { ability: 'families.read' } satisfies AbilityHandle,
    Component: RequireAbility,
    children: [
      {
        index: true,
        lazy: async () => ({ Component: (await import('../features/families/family-detail-page')).default }),
      },
    ],
  },
];

const regionRoutes: RouteObject[] = [
  {
    path: 'wilayah',
    // One ability. It was two for a while — a Staf reached this screen on
    // `users.assignRegion` to move a coordinator between regions — and that
    // action no longer exists, so the gate is a single ability again.
    handle: { ability: 'regions.manage' } satisfies AbilityHandle,
    Component: RequireAbility,
    children: [
      {
        index: true,
        lazy: async () => ({ Component: (await import('../features/regions/region-list-page')).default }),
      },
    ],
  },
];

/**
 * The approval queue, users and church settings (docs/07-implementation-roadmap.md
 * F5), replaced in place like every section before them: the paths, sidebar
 * entries and gates are exactly as F1 left them, and only the `lazy` imports
 * changed.
 *
 * None of the three has a child route. The queue reviews in a dialog rather than
 * on its own page — a reviewer works down a list and should not lose their place
 * in it — and both admin screens edit in dialogs for the same reason.
 */
const adminRoutes: RouteObject[] = [
  {
    path: 'permintaan-perubahan',
    handle: { ability: 'changeRequests.list' } satisfies AbilityHandle,
    Component: RequireAbility,
    children: [
      {
        index: true,
        lazy: async () => ({
          Component: (await import('../features/change-requests/change-request-list-page')).default,
        }),
      },
    ],
  },
  {
    path: 'pengguna',
    handle: { ability: 'users.list' } satisfies AbilityHandle,
    Component: RequireAbility,
    children: [
      {
        index: true,
        lazy: async () => ({ Component: (await import('../features/users/user-list-page')).default }),
      },
    ],
  },
  {
    /**
     * The two attestation desks (`docs/02` §1.3).
     *
     * Each is gated on the ability the work itself needs rather than one of its
     * own: arriving is a member creation, leaving is a bulk status change. So
     * the screen and the requests behind it admit exactly the same people, and
     * a coordinator stays region-scoped without this route knowing what a
     * region is.
     */
    path: 'atestasi-masuk',
    handle: { ability: 'members.create' } satisfies AbilityHandle,
    Component: RequireAbility,
    children: [
      {
        index: true,
        lazy: async () => ({ Component: (await import('../features/attestation/attestation-in-page')).default }),
      },
      {
        /**
         * The unfinished intakes, on their own screen rather than stacked above
         * the form. Same gate as the form: a draft is a member creation that
         * has not happened yet, and the endpoints behind it (`/intake/drafts`)
         * admit exactly the roles this route does.
         */
        path: 'draf',
        lazy: async () => ({ Component: (await import('../features/attestation/intake-drafts-page')).default }),
      },
    ],
  },
  {
    path: 'atestasi-keluar',
    handle: { ability: 'members.changeStatus' } satisfies AbilityHandle,
    Component: RequireAbility,
    children: [
      {
        index: true,
        lazy: async () => ({ Component: (await import('../features/attestation/attestation-out-page')).default }),
      },
    ],
  },
  {
    /**
     * Reporting (F6.2, F7.10) — a **sidebar section**, not a screen. Its
     * destinations are gated one by one, each on the ability its own endpoint
     * needs: the birthday roster on `members.birthdays`, the wedding-anniversary
     * roster on `members.anniversaries`, the attestation register and the
     * register of deaths on `reports.read`. `RequireAbility` takes the innermost
     * `handle` among the matched routes, so the section admits anyone who may
     * open any of them and each child narrows to its own.
     *
     * The three abilities admit the same three roles today and are free to
     * stop. Gating any of them on whichever is convenient is how a screen ends
     * up refused by an endpoint the route never checked.
     *
     * **Nothing renders at `/laporan` itself.** It redirects, because the
     * sidebar picks its active entry with `startsWith` and a parent path beside
     * its own child would match first and light up the wrong row. The redirect
     * is also what keeps `/laporan` — the address this section had when it was
     * one report — working for anyone who bookmarked it.
     */
    path: 'laporan',
    handle: { ability: ['members.birthdays', 'members.anniversaries', 'reports.read'] } satisfies AbilityHandle,
    Component: RequireAbility,
    children: [
      { index: true, element: <Navigate to="/laporan/ulang-tahun" replace /> },
      {
        path: 'ulang-tahun',
        handle: { ability: 'members.birthdays' } satisfies AbilityHandle,
        Component: RequireAbility,
        children: [
          {
            index: true,
            lazy: async () => ({ Component: (await import('../features/reports/birthday-report-page')).default }),
          },
        ],
      },
      {
        path: 'ulang-tahun-pernikahan',
        handle: { ability: 'members.anniversaries' } satisfies AbilityHandle,
        Component: RequireAbility,
        children: [
          {
            index: true,
            lazy: async () => ({ Component: (await import('../features/reports/anniversary-report-page')).default }),
          },
        ],
      },
      {
        path: 'atestasi',
        handle: { ability: 'reports.read' } satisfies AbilityHandle,
        Component: RequireAbility,
        children: [
          {
            index: true,
            lazy: async () => ({ Component: (await import('../features/reports/attestation-report-page')).default }),
          },
        ],
      },
      {
        path: 'kematian',
        handle: { ability: 'reports.read' } satisfies AbilityHandle,
        Component: RequireAbility,
        children: [
          {
            index: true,
            lazy: async () => ({ Component: (await import('../features/reports/death-register-page')).default }),
          },
        ],
      },
    ],
  },
  {
    /** The audit log viewer (F6.5). SUPER_ADMIN only, and not region-scoped. */
    path: 'catatan-audit',
    handle: { ability: 'auditLogs.read' } satisfies AbilityHandle,
    Component: RequireAbility,
    children: [
      {
        index: true,
        lazy: async () => ({ Component: (await import('../features/audit-logs/audit-log-page')).default }),
      },
    ],
  },
  {
    /**
     * Gated on `church.read`, which is STAFF *and* SUPER_ADMIN — so a STAFF
     * member reaches this screen, and the writes inside it are theirs to be
     * refused: `PATCH /church` and `PATCH /church/settings` are SUPER_ADMIN-only
     * (docs/04 §3b). That mismatch is the sidebar entry F1 shipped, and it is
     * left as it is rather than narrowed here — see the F5 notes in docs/09.
     */
    path: 'pengaturan',
    handle: { ability: 'church.read' } satisfies AbilityHandle,
    Component: RequireAbility,
    children: [
      {
        index: true,
        lazy: async () => ({ Component: (await import('../features/church/church-settings-page')).default }),
      },
    ],
  },
];

/** Navigation entries whose screens are built; everything else still lands on the placeholder. */
const BUILT_SCREENS = new Set([
  '/atestasi-masuk',
  '/atestasi-keluar',
  '/jemaat',
  '/keluarga',
  '/wilayah',
  '/permintaan-perubahan',
  '/pengguna',
  '/pengaturan',
  // F6 replaced the last two placeholders. `plannedScreens` below now resolves
  // to an empty list — kept rather than deleted, because F7.6 and anything after
  // it will route a screen before building it the same way.
  //
  // Every entry of the Laporan section is named, not the section: this set is
  // keyed by `NAV_ITEMS.path`, and since F7.10 no navigation entry points at
  // `/laporan` itself. Leaving the old entry here would have matched nothing
  // and quietly routed a coming-soon placeholder over each real report.
  '/laporan/ulang-tahun',
  '/laporan/ulang-tahun-pernikahan',
  '/laporan/atestasi',
  '/laporan/kematian',
  '/catatan-audit',
]);

/**
 * The screens later sprints build. They are routed **now**, at their real paths
 * and behind their real gates, because the sidebar is real now: an entry leading
 * to a 404 is worse than one that says which sprint it is waiting for, and
 * having them routed is what gives F1.12's per-role navigation test something to
 * walk. Each is replaced in place by the sprint that owns it — swap the child's
 * `lazy` import and the path, gate and navigation entry stay as they are.
 */
const plannedScreens: RouteObject[] = NAV_ITEMS.flatMap((item) =>
  item.ability === null || BUILT_SCREENS.has(item.path)
    ? []
    : [
        {
          // Relative to the layout route's `/`.
          path: item.path.slice(1),
          handle: { ability: item.ability } satisfies AbilityHandle,
          Component: RequireAbility,
          children: [
            {
              index: true,
              lazy: async () => ({ Component: (await import('./pages/coming-soon')).default }),
            },
          ],
        },
      ],
);

export const routes: RouteObject[] = [
  {
    // The screens reachable without a session. `/diagnostics` is deliberately
    // among them: it exists to investigate a deployment that is misbehaving
    // (F0.11), and a diagnostics page that can only be opened by someone already
    // signed in is no use on the day signing in is what broke.
    ErrorBoundary: RouteErrorBoundary,
    children: [
      {
        path: '/masuk',
        lazy: async () => ({ Component: (await import('../features/auth/login-page')).default }),
      },
      {
        path: '/lupa-sandi',
        lazy: async () => ({ Component: (await import('../features/auth/forgot-password-page')).default }),
      },
      {
        path: '/atur-ulang-sandi',
        lazy: async () => ({ Component: (await import('../features/auth/reset-password-page')).default }),
      },
      {
        path: '/undangan',
        lazy: async () => ({ Component: (await import('../features/auth/register-with-invite-page')).default }),
      },
      {
        path: '/diagnostics',
        lazy: async () => ({ Component: (await import('./pages/diagnostics')).default }),
      },
      // A mistyped address is not privileged information, so it reads as a 404
      // whether or not anyone is signed in, rather than as a demand to sign in
      // before being told the page does not exist.
      { path: '*', Component: NotFound },
    ],
  },
  {
    Component: RequireAuth,
    children: [
      {
        path: '/',
        Component: AppShell,
        ErrorBoundary: RouteErrorBoundary,
        children: [
          {
            index: true,
            lazy: async () => ({ Component: (await import('./pages/home')).default }),
          },
          ...memberRoutes,
          ...familyRoutes,
          ...regionRoutes,
          ...adminRoutes,
          ...plannedScreens,
        ],
      },
    ],
  },
];

export function createRouter() {
  return createBrowserRouter(routes);
}
